import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { createProduct, updateProduct, fetchCategories } from '../../store/slices/inventorySlice';
import {
  FORACARE_METER_SERIAL_ERROR,
  FORACARE_METER_SERIAL_LENGTH,
  getForaCareMeterSerialError,
  sanitizeForaCareMeterSerialInput
} from '../../utils/foraCareMeterUtils';
import './InventoryProductModal.css';
import '../../pages/PracticeManagement/PracticesList/AddPractice/AddPractice.css';
import AppDateInput from '../Common/AppDateInput/AppDateInput';

const INITIAL_FORM = {
  product_category: '',
  product_name: '',
  model_number: '',
  price: '',
  qty_of_each_category: '',
  received_date: '',
  gateway_serial_number: '',
  status: ''
};

const normalizeToInputDate = (value) => {
  if (!value) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const slashFormat = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value);
  if (slashFormat) {
    const part1 = Number(slashFormat[1]);
    const part2 = Number(slashFormat[2]);
    const year = slashFormat[3];
    const month = part1 > 12 ? part2 : part1;
    const day = part1 > 12 ? part1 : part2;
    const mm = String(month).padStart(2, '0');
    const dd = String(day).padStart(2, '0');
    return `${year}-${mm}-${dd}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const y = parsed.getFullYear();
  const m = String(parsed.getMonth() + 1).padStart(2, '0');
  const d = String(parsed.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const onlyDigits = (value) =>
  sanitizeForaCareMeterSerialInput(value);

const onlyAlphanumeric = (value) =>
  String(value ?? '')
    .replace(/[^a-zA-Z0-9]/g, '')
    .slice(0, 128);

/** Virtual serial list: row height (px) for scroll positioning. */
const SERIAL_ROW_HEIGHT = 72;
const SERIAL_LIST_MAX_HEIGHT = 440;
const SERIAL_OVERSCAN = 10;

const trimSerialMapFromIndex = (map, fromIdx) => {
  for (const k of [...map.keys()]) {
    if (k >= fromIdx) map.delete(k);
  }
};

/** Edit load: fill map from API without allocating a huge dense array of empty strings. */
const seedSerialMapFromProduct = (product, qty, map) => {
  map.clear();
  if (!product || qty <= 0) return;
  let list = [];
  if (Array.isArray(product.serial_numbers) && product.serial_numbers.length > 0) {
    list = product.serial_numbers.map((s) => (s == null ? '' : String(s)));
  } else if (Array.isArray(product.mapped_devices) && product.mapped_devices.length > 0) {
    list = [...product.mapped_devices]
      .sort((a, b) => (Number(a.id) || 0) - (Number(b.id) || 0))
      .map((d) => (d.serial_number == null ? '' : String(d.serial_number)));
  }
  const n = Math.min(list.length, qty);
  for (let i = 0; i < n; i += 1) {
    const v = onlyDigits(list[i]);
    if (v) map.set(i, v);
  }
};

const InventoryProductModal = ({ show, onClose, mode = 'add', product = null }) => {
  const dispatch = useDispatch();
  const { categories = [], isSaving } = useSelector((state) => state.inventory || {});

  const [formData, setFormData] = useState(() => ({ ...INITIAL_FORM }));
  const serialMapRef = useRef(new Map());
  /** Bumps when serial map changes so virtual rows re-read values. */
  const [serialTick, setSerialTick] = useState(0);
  const [serialScrollTop, setSerialScrollTop] = useState(0);
  const serialScrollRef = useRef(null);
  const [errors, setErrors] = useState({});

  const bumpSerials = useCallback(() => {
    setSerialTick((t) => t + 1);
  }, []);

  const resetSerialScroll = useCallback(() => {
    setSerialScrollTop(0);
    if (serialScrollRef.current) serialScrollRef.current.scrollTop = 0;
  }, []);

  const isEditMode = mode === 'edit';
  const sectionTitle = isEditMode ? 'Edit Products' : 'Add Products';
  const submitLoadingLabel = isEditMode ? 'Updating...' : 'Adding...';
  const submitIdleLabel = isEditMode ? 'OK' : 'Add';

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!show) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [show]);

  useEffect(() => {
    if (!show) return;

    if (isEditMode && product) {
      const rawQty = parseInt(String(product.qty ?? product.qty_of_each_category ?? ''), 10) || 0;
      setFormData({
        product_category: product.product_category || '',
        product_name: product.product_name || '',
        model_number: product.model_number || '',
        price: product.price != null && product.price !== '' ? String(product.price) : '',
        qty_of_each_category: rawQty > 0 ? String(rawQty) : '',
        received_date: normalizeToInputDate(product.received_date),
        gateway_serial_number: onlyAlphanumeric(product.gateway_serial_number || ''),
        status: product.status || ''
      });
      seedSerialMapFromProduct(product, rawQty, serialMapRef.current);
      resetSerialScroll();
      bumpSerials();
      setErrors({});
      return;
    }

    setFormData({ ...INITIAL_FORM });
    serialMapRef.current.clear();
    resetSerialScroll();
    bumpSerials();
    setErrors({});
  }, [show, isEditMode, product, bumpSerials, resetSerialScroll]);

  const qtyValue = parseInt(formData.qty_of_each_category || '0', 10) || 0;
  /** Add flow: enter new serials. Edit flow: serials are read-only (shown below). */
  const showAddSerialSection = !isEditMode && qtyValue > 0;

  const handleClose = () => {
    onClose();
  };

  const handleChange = (e) => {
    const { name } = e.target;
    let { value } = e.target;

    if (name === 'price') {
      value = onlyDigits(value);
    }
    if (name === 'gateway_serial_number') {
      value = onlyAlphanumeric(value);
    }

    if (name === 'product_category' && !value) {
      serialMapRef.current.clear();
      bumpSerials();
    }

    if (name === 'qty_of_each_category') {
      if (value === '') {
        setFormData((prev) => ({ ...prev, [name]: '' }));
        if (errors[name]) {
          setErrors((prev) => ({ ...prev, [name]: '' }));
        }
        return;
      }
      const raw = parseInt(String(value), 10);
      if (Number.isNaN(raw) || raw < 0) return;
      setFormData((prev) => {
        const prevQty = parseInt(prev.qty_of_each_category || '0', 10) || 0;
        trimSerialMapFromIndex(serialMapRef.current, raw);
        if (raw !== prevQty) resetSerialScroll();
        bumpSerials();
        return { ...prev, [name]: String(raw) };
      });
      if (errors[name]) {
        setErrors((prev) => ({ ...prev, [name]: '' }));
      }
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleQtyBlur = () => {
    if (!formData.qty_of_each_category) {
      serialMapRef.current.clear();
      resetSerialScroll();
      bumpSerials();
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.product_name.trim()) newErrors.product_name = 'Product Name is required';
    if (!formData.model_number.trim()) newErrors.model_number = 'Model Number is required';
    if (!formData.price) newErrors.price = 'Price is required';
    if (!formData.qty_of_each_category) newErrors.qty_of_each_category = 'Quantity is required';
    if (!formData.received_date) newErrors.received_date = 'Received Date is required';
    if (!formData.status) newErrors.status = 'Status is required';

    if (formData.price && !/^\d+$/.test(String(formData.price))) {
      newErrors.price = 'Price must contain digits only';
    }

    if (
      formData.gateway_serial_number &&
      !/^[a-zA-Z0-9]+$/.test(String(formData.gateway_serial_number).trim())
    ) {
      newErrors.gateway_serial_number = 'Gateway Serial Number must be alphanumeric (letters and numbers only)';
    }

    const qty = parseInt(formData.qty_of_each_category || '0', 10);
    if (!Number.isInteger(qty) || qty <= 0) {
      newErrors.qty_of_each_category = 'Quantity must be greater than 0';
    }

    if (showAddSerialSection && qty > 0) {
      const map = serialMapRef.current;
      let missing = false;
      let invalid = false;
      for (let i = 0; i < qty; i += 1) {
        const sn = map.get(i) ?? '';
        if (!String(sn).trim()) missing = true;
        else if (getForaCareMeterSerialError(sn)) invalid = true;
        if (missing && invalid) break;
      }
      if (missing) {
        newErrors.serial_numbers = `Please enter ${qty} serial number(s)`;
      } else if (invalid) {
        newErrors.serial_numbers = FORACARE_METER_SERIAL_ERROR;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    const submitQty = parseInt(formData.qty_of_each_category || '0', 10) || 0;
    const serialList =
      showAddSerialSection && submitQty > 0
        ? Array.from({ length: submitQty }, (_, i) => serialMapRef.current.get(i) ?? '')
        : [];

    try {
      if (isEditMode && product?.id) {
        await dispatch(
          updateProduct({
            productId: product.id,
            productData: {
              product_name: formData.product_name,
              price: formData.price,
              received_date: formData.received_date,
              gateway_serial_number: formData.gateway_serial_number,
              status: formData.status
            }
          })
        ).unwrap();
      } else {
        await dispatch(
          createProduct({
            ...formData,
            qty_of_each_category: String(submitQty),
            serial_numbers: serialList
          })
        ).unwrap();
      }
      onClose({ saved: true });
    } catch (err) {
      void err;
    }
  };

  const modalClass = useMemo(() => {
    if (!showAddSerialSection && !(isEditMode && qtyValue > 0)) return 'ipm-card';
    return 'ipm-card ipm-card--with-serial';
  }, [showAddSerialSection, isEditMode, qtyValue]);

  const editSerialDisplayRows = useMemo(() => {
    if (!isEditMode || !qtyValue) return [];
    const m = serialMapRef.current;
    return Array.from({ length: qtyValue }, (_, i) => onlyDigits(String(m.get(i) ?? '')));
  }, [isEditMode, qtyValue, serialTick, product?.id]);

  const virtualSerial = useMemo(() => {
    if (!showAddSerialSection || qtyValue <= 0) {
      return { indices: [] };
    }
    const startRow = Math.floor(serialScrollTop / SERIAL_ROW_HEIGHT);
    const visibleCount = Math.ceil(SERIAL_LIST_MAX_HEIGHT / SERIAL_ROW_HEIGHT) + SERIAL_OVERSCAN * 2;
    const from = Math.max(0, startRow - SERIAL_OVERSCAN);
    const to = Math.min(qtyValue, from + visibleCount);
    const indices = [];
    for (let idx = from; idx < to; idx += 1) indices.push(idx);
    return { indices };
  }, [showAddSerialSection, qtyValue, serialScrollTop, serialTick]);

  useEffect(() => {
    const el = serialScrollRef.current;
    if (!el || !showAddSerialSection || qtyValue <= 0) return;
    const maxScroll = Math.max(0, qtyValue * SERIAL_ROW_HEIGHT - SERIAL_LIST_MAX_HEIGHT);
    if (el.scrollTop > maxScroll) {
      el.scrollTop = maxScroll;
      setSerialScrollTop(maxScroll);
    }
  }, [qtyValue, showAddSerialSection, serialTick]);

  if (!show) return null;
  if (isEditMode && !product) return null;

  return (
    <div
      className="ipm-overlay"
      onClick={(e) => e.stopPropagation()}
      onWheel={(e) => e.stopPropagation()}
      onTouchMove={(e) => e.stopPropagation()}
    >
      <div className={modalClass} onClick={(e) => e.stopPropagation()}>
        <div className="ipm-head">
          <div />
          <button type="button" className="ipm-close" onClick={handleClose} aria-label="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <form className="ipm-form" onSubmit={handleSubmit}>
          <div className="ipm-body">
            <section className="ipm-section">
              <h5 className="ipm-section-title">{sectionTitle}</h5>

              <div className="ipm-grid">
                <div className="ipm-col">
                  <div className="ipm-field">
                    <label className="ipm-label">Product Category</label>
                    <select
                      className={`ipm-control ${errors.product_category ? 'ipm-control--invalid' : ''}`}
                      name="product_category"
                      value={formData.product_category}
                      onChange={handleChange}
                      disabled={isEditMode}
                    >
                      <option value="">Select category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                    {errors.product_category && <div className="ipm-error">{errors.product_category}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Product Name <span className="ipm-req">*</span></label>
                    <input
                      type="text"
                      className={`ipm-control ${errors.product_name ? 'ipm-control--invalid' : ''}`}
                      name="product_name"
                      value={formData.product_name}
                      onChange={handleChange}
                    />
                    {errors.product_name && <div className="ipm-error">{errors.product_name}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Model Number <span className="ipm-req">*</span></label>
                    <input
                      type="text"
                      className={`ipm-control ${errors.model_number ? 'ipm-control--invalid' : ''}`}
                      name="model_number"
                      value={formData.model_number}
                      onChange={handleChange}
                      disabled={isEditMode}
                      readOnly={isEditMode}
                    />
                    {errors.model_number && <div className="ipm-error">{errors.model_number}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Price($) <span className="ipm-req">*</span></label>
                    <input
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      className={`ipm-control ${errors.price ? 'ipm-control--invalid' : ''}`}
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                    />
                    {errors.price && <div className="ipm-error">{errors.price}</div>}
                  </div>
                </div>

                <div className="ipm-col">
                  <div className="ipm-field">
                    <label className="ipm-label">
                      Qty of Each Category <span className="ipm-req">*</span>
                    </label>
                    <input
                      type="number"
                      min={1}
                      className={`ipm-control ${errors.qty_of_each_category ? 'ipm-control--invalid' : ''}`}
                      name="qty_of_each_category"
                      value={formData.qty_of_each_category}
                      onChange={handleChange}
                      onBlur={handleQtyBlur}
                      disabled={isEditMode}
                      readOnly={isEditMode}
                    />
                    {errors.qty_of_each_category && <div className="ipm-error">{errors.qty_of_each_category}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Received Date <span className="ipm-req">*</span></label>
                    <AppDateInput
                      className={`ipm-control ${errors.received_date ? 'ipm-control--invalid' : ''}`}
                      name="received_date"
                      value={formData.received_date}
                      onChange={(v) => handleChange({ target: { name: 'received_date', value: v } })}
                    />
                    {errors.received_date && <div className="ipm-error">{errors.received_date}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Gateway Serial Number</label>
                    <input
                      type="text"
                      inputMode="text"
                      autoComplete="off"
                      spellCheck={false}
                      className={`ipm-control ${errors.gateway_serial_number ? 'ipm-control--invalid' : ''}`}
                      name="gateway_serial_number"
                      value={formData.gateway_serial_number}
                      onChange={handleChange}
                    />
                    {errors.gateway_serial_number && <div className="ipm-error">{errors.gateway_serial_number}</div>}
                  </div>

                  <div className="ipm-field">
                    <label className="ipm-label">Status <span className="ipm-req">*</span></label>
                    <select
                      className={`ipm-control ${errors.status ? 'ipm-control--invalid' : ''}`}
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="">Select status</option>
                      <option value="Active">Active</option>
                      <option value="Inactive">Inactive</option>
                    </select>
                    {errors.status && <div className="ipm-error">{errors.status}</div>}
                  </div>
                </div>
              </div>

              {showAddSerialSection && (
                <div className="ipm-serial">
                  <div
                    ref={serialScrollRef}
                    className="ipm-serial-virtual-scroll"
                    onScroll={(e) => setSerialScrollTop(e.currentTarget.scrollTop)}
                  >
                    <div
                      className="ipm-serial-virtual-inner"
                      style={{ height: qtyValue * SERIAL_ROW_HEIGHT, position: 'relative' }}
                    >
                      {virtualSerial.indices.map((idx) => {
                        const serial = serialMapRef.current.get(idx) ?? '';
                        return (
                          <div
                            key={`serial-${qtyValue}-${idx}`}
                            className="ipm-serial-field ipm-serial-field--virtual"
                            style={{
                              position: 'absolute',
                              top: idx * SERIAL_ROW_HEIGHT,
                              left: 0,
                              right: 0,
                              height: SERIAL_ROW_HEIGHT,
                              boxSizing: 'border-box'
                            }}
                          >
                            <label className="ipm-label ipm-serial-field-label">
                              Add serial number {idx + 1} <span className="ipm-req">*</span>
                            </label>
                            <input
                              type="text"
                              className={`ipm-control ${errors.serial_numbers ? 'ipm-control--invalid' : ''}`}
                              placeholder={`Enter 16-digit serial number ${idx + 1}`}
                              value={serial}
                              maxLength={FORACARE_METER_SERIAL_LENGTH}
                              inputMode="numeric"
                              onChange={(e) => {
                                const value = onlyDigits(e.target.value);
                                const m = serialMapRef.current;
                                if (value) m.set(idx, value);
                                else m.delete(idx);
                                bumpSerials();
                                if (errors.serial_numbers) {
                                  setErrors((prev) => ({ ...prev, serial_numbers: '' }));
                                }
                              }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {errors.serial_numbers && <div className="ipm-error ipm-error--block">{errors.serial_numbers}</div>}
                </div>
              )}

              {isEditMode && qtyValue > 0 && (
                <div className="ipm-serial ipm-serial-readonly mt-2">
                  <label className="ipm-label">Serial numbers on file</label>
                  <p className="ipm-hint small text-muted mb-2">Assigned serials cannot be changed from this screen.</p>
                  <ul className="list-unstyled mb-0">
                    {editSerialDisplayRows.map((sn, idx) => (
                      <li key={`edit-sn-${idx}`} className="mb-1">
                        <span className="text-muted">Serial number {idx + 1}:</span>{' '}
                        <span className="fw-medium">{sn || '—'}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>

          <div className="ipm-foot add-practice-form-actions">
            <div className="ipm-actions">
              <button
                type="button"
                className="add-practice-btn add-practice-btn-cancel"
                onClick={handleClose}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="add-practice-btn add-practice-btn-add"
                disabled={isSaving}
              >
                {isSaving ? (
                  <>
                    <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    {submitLoadingLabel}
                  </>
                ) : (
                  submitIdleLabel
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default InventoryProductModal;
